/**
 * Azure CLI wrapper for app registration
 *
 * Creates and manages the multi-tenant public client app registration
 * used by Power Automate MCP. Uses execFile (not exec) for command
 * injection safety — same pattern as openBrowser in wam-broker.ts.
 */

import { execFile } from "child_process";
import {
  REDIRECT_URI,
  getRequiredResourceAccessForFeatures,
} from "./published-app.js";
import type { FeatureId } from "../config/features.js";
import { WAM_REDIRECT_URI_TEMPLATE } from "../utils/constants.js";

// UUID regex for validating client IDs from Azure CLI output
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Run an Azure CLI command safely via execFile.
 * Windows requires routing through cmd.exe; macOS/Linux call az directly.
 * Exported for the tenant-provisioning helpers in graph-provisioning.ts.
 */
export function runAzCommand(args: string[]): Promise<string> {
  return new Promise((resolve, reject) => {
    const isWindows = process.platform === "win32";
    const cmd = isWindows ? "cmd.exe" : "az";
    const cmdArgs = isWindows ? ["/c", "az", ...args] : args;

    execFile(cmd, cmdArgs, { timeout: 60_000 }, (error, stdout, stderr) => {
      if (error) {
        const msg = stderr?.trim() || error.message;
        reject(new Error(msg));
      } else {
        resolve(stdout.trim());
      }
    });
  });
}

/**
 * Check if Azure CLI is installed and accessible.
 */
export async function checkAzureCli(): Promise<boolean> {
  try {
    await runAzCommand(["version", "--output", "json"]);
    return true;
  } catch {
    return false;
  }
}

/**
 * Ensure Azure CLI has an active login session.
 * If not logged in, triggers `az login` which opens a browser.
 * Returns the logged-in username.
 */
export async function ensureAzureCliLogin(): Promise<string> {
  // Check current session
  try {
    const output = await runAzCommand(["account", "show", "--output", "json"]);
    const account = JSON.parse(output) as { user?: { name?: string } };
    if (account.user?.name) {
      return account.user.name;
    }
  } catch {
    // Not logged in — fall through to az login
  }

  // Trigger interactive login (opens browser)
  const output = await runAzCommand(["login", "--output", "json"]);
  const accounts = JSON.parse(output) as Array<{ user?: { name?: string } }>;
  const name = accounts[0]?.user?.name;
  if (!name) {
    throw new Error("Azure CLI login succeeded but no account name returned");
  }
  return name;
}

/**
 * Search for an existing app registration by display name.
 * Returns the appId if found, null otherwise.
 */
export async function findExistingApp(displayName: string): Promise<string | null> {
  try {
    // Escape single quotes in displayName to prevent OData filter injection
    const sanitizedName = displayName.replace(/'/g, "''");
    const output = await runAzCommand([
      "ad", "app", "list",
      "--filter", `displayName eq '${sanitizedName}'`,
      "--query", "[0].appId",
      "--output", "tsv",
    ]);
    const appId = output.trim();
    if (appId && UUID_RE.test(appId)) {
      return appId;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Create a new multi-tenant public client app registration.
 * Mirrors the PowerShell script: multi-tenant, public client, delegated permissions.
 * Returns the new app's clientId.
 */
export async function createAppRegistration(
  displayName: string,
  features?: readonly FeatureId[]
): Promise<string> {
  const resourceAccessJson = JSON.stringify(getRequiredResourceAccessForFeatures(features));

  const output = await runAzCommand([
    "ad", "app", "create",
    "--display-name", displayName,
    "--sign-in-audience", "AzureADMultipleOrgs",
    "--enable-access-token-issuance", "false",
    "--enable-id-token-issuance", "false",
    "--is-fallback-public-client", "true",
    "--public-client-redirect-uris", REDIRECT_URI,
    "--required-resource-accesses", resourceAccessJson,
    "--output", "json",
  ]);

  const app = JSON.parse(output) as { appId?: string };
  const clientId = app.appId;
  if (!clientId || !UUID_RE.test(clientId)) {
    throw new Error("App registration created but no valid appId returned");
  }

  return clientId;
}

/**
 * Add the WAM broker redirect URI to an existing app registration.
 * The WAM URI contains the clientId itself: ms-appx-web://microsoft.aad.brokerplugin/{clientId}
 */
export async function addWamRedirectUri(clientId: string): Promise<void> {
  if (!UUID_RE.test(clientId)) {
    throw new Error(`Invalid client ID format: ${clientId}`);
  }

  const wamUri = WAM_REDIRECT_URI_TEMPLATE.replace("{clientId}", clientId);

  await runAzCommand([
    "ad", "app", "update",
    "--id", clientId,
    "--public-client-redirect-uris", REDIRECT_URI, wamUri,
  ]);
}
